import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import useIsMounted from "@entrilia/shared/hooks/useIsMounted";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import adminApi from "../../../api/adminApi";
import type { CreateDataImportRequest, DataImportInfo, DataImportMetadata } from "../../../api/types/dataImportTypes";
import type { OperationStatus, StartDataImportOptions } from "./importDataPagesTypes";

const createDataImport = withErrorHandling(adminApi.createDataImport);
const startDataImportParsingAndValidation = withErrorHandling(adminApi.startDataImportParsingAndValidation);
const getDataImportValidationStatus = withErrorHandling(adminApi.getDataImportValidationStatus);
const getDataImportDetails = withErrorHandling(adminApi.getDataImportDetails);
const getDataImportMetadata = withErrorHandling(adminApi.getDataImportMetadata);
const startApplyingDataImport = withErrorHandling(adminApi.startApplyingDataImport);
const getDataImportApplicationStatus = withErrorHandling(adminApi.getDataImportApplicationStatus);
const startDataImportRollback = withErrorHandling(adminApi.startDataImportRollback);
const getDataImportRollbackStatus = withErrorHandling(adminApi.getDataImportRollbackStatus);

const delayMs = 1000;

export interface DataImportResult {
  dataImport: DataImportInfo;
  dataImportMetadata: DataImportMetadata;
}

interface CreateDataImportOptions {
  onStatusUpdate: (operationStatus: OperationStatus) => void;
  onComplete: (result: DataImportResult) => void;
  onError: (error: ApiError) => void;
}

interface ValidationPollingState {
  dataImportId: string;
  validationId: string;
}

export const useCreateDataImport = ({ onStatusUpdate, onComplete, onError }: CreateDataImportOptions) => {
  const isMounted = useIsMounted();
  const [pollingState, setPollingState] = useState<ValidationPollingState>();

  const poll = useCallback(async () => {
    if (!pollingState) {
      return;
    }

    const [statusResp, statusError] = await getDataImportValidationStatus(
      pollingState.dataImportId,
      pollingState.validationId
    );

    if (statusError?.type === "NotFound") {
      return;
    }

    if (statusError) {
      setPollingState(undefined);
      onError(statusError);
      return;
    }

    onStatusUpdate({
      statusText: statusResp.status,
      error: statusResp.error,
      details: [
        ["Processed records", statusResp.processedRecords.toLocaleString()],
        ["Invalid records", statusResp.invalidRecords.toLocaleString()],
      ],
    });

    if (statusResp.isFinished) {
      setPollingState(undefined);

      if (statusResp.error) {
        onError({ message: statusResp.error });
        return;
      }

      const [[dataImport, dataImportError], [dataImportMetadata, metadataError]] = await Promise.all([
        getDataImportDetails(pollingState.dataImportId),
        getDataImportMetadata(pollingState.dataImportId),
      ]);

      const fetchError = dataImportError || metadataError;
      if (fetchError) {
        onError(fetchError);
      } else {
        onComplete({ dataImport, dataImportMetadata });
      }
    }
  }, [pollingState, onStatusUpdate, onComplete, onError]);

  useSetInterval(poll, delayMs, { disabled: !pollingState });

  return useCallback(
    async (req: CreateDataImportRequest) => {
      onStatusUpdate({ statusText: "Creating data import", details: [] });

      const [createdDataImport, createError] = await createDataImport(req);
      if (createError) {
        onError(createError);
        return;
      }

      const [parseResp, parseError] = await startDataImportParsingAndValidation(createdDataImport.id);
      if (parseError) {
        onError(parseError);
        return;
      }

      if (isMounted()) {
        setPollingState({ dataImportId: createdDataImport.id, validationId: parseResp.validationId });
      }
    },
    [isMounted, onStatusUpdate, onError]
  );
};

interface ValidateDataImportOptions {
  onStatusUpdate: (operationStatus: OperationStatus) => void;
  onComplete: (result: DataImportResult) => void;
  onError: (error: ApiError) => void;
}

export const useValidateDataImport = ({ onStatusUpdate, onComplete, onError }: ValidateDataImportOptions) => {
  const isMounted = useIsMounted();
  const [pollingState, setPollingState] = useState<ValidationPollingState>();

  const poll = useCallback(async () => {
    if (!pollingState) {
      return;
    }

    const [statusResp, statusError] = await getDataImportValidationStatus(
      pollingState.dataImportId,
      pollingState.validationId
    );

    if (statusError?.type === "NotFound") {
      return;
    }

    if (statusError) {
      setPollingState(undefined);
      onError(statusError);
      return;
    }

    onStatusUpdate({
      statusText: statusResp.status,
      error: statusResp.error,
      details: [
        ["Processed records", statusResp.processedRecords.toLocaleString()],
        ["Invalid records", statusResp.invalidRecords.toLocaleString()],
      ],
    });

    if (statusResp.isFinished) {
      setPollingState(undefined);

      if (statusResp.error) {
        onError({ message: statusResp.error });
        return;
      }

      const [[dataImport, dataImportError], [dataImportMetadata, metadataError]] = await Promise.all([
        getDataImportDetails(pollingState.dataImportId),
        getDataImportMetadata(pollingState.dataImportId),
      ]);

      const fetchError = dataImportError || metadataError;
      if (fetchError) {
        onError(fetchError);
      } else {
        onComplete({ dataImport, dataImportMetadata });
      }
    }
  }, [pollingState, onStatusUpdate, onComplete, onError]);

  useSetInterval(poll, delayMs, { disabled: !pollingState });

  return useCallback(
    async (importId: string) => {
      onStatusUpdate({ statusText: "Validating data import", details: [] });

      const [parseResp, parseError] = await startDataImportParsingAndValidation(importId);
      if (parseError) {
        onError(parseError);
        return;
      }

      if (isMounted()) {
        setPollingState({ dataImportId: importId, validationId: parseResp.validationId });
      }
    },
    [isMounted, onStatusUpdate, onError]
  );
};

interface ApplyDataImportOptions {
  onStatusUpdate: (operationStatus: OperationStatus) => void;
  onComplete: () => void;
  onError: (error: ApiError) => void;
}

interface ApplicationPollingState {
  dataImportId: string;
  applicationId: string;
}

export const useApplyDataImport = ({ onStatusUpdate, onComplete, onError }: ApplyDataImportOptions) => {
  const isMounted = useIsMounted();
  const [pollingState, setPollingState] = useState<ApplicationPollingState>();

  const poll = useCallback(async () => {
    if (!pollingState) {
      return;
    }

    const [statusResp, statusError] = await getDataImportApplicationStatus(
      pollingState.dataImportId,
      pollingState.applicationId
    );

    if (statusError?.type === "NotFound") {
      return;
    }

    if (statusError) {
      setPollingState(undefined);
      onError(statusError);
      return;
    }

    onStatusUpdate({
      statusText: statusResp.status,
      error: statusResp.error,
      details: [
        ["Processed records", statusResp.processedRecords.toLocaleString()],
        ["Failed records", statusResp.failedRecords.toLocaleString()],
        ["Excluded records", statusResp.excludedRecords.toLocaleString()],
      ],
    });

    if (statusResp.isCompleted) {
      setPollingState(undefined);

      if (statusResp.error) {
        onError({ message: statusResp.error });
      } else {
        onComplete();
      }
    }
  }, [pollingState, onStatusUpdate, onComplete, onError]);

  useSetInterval(poll, delayMs, { disabled: !pollingState });

  return useCallback(
    async (importId: string, options: StartDataImportOptions) => {
      onStatusUpdate({ statusText: "Applying data import", details: [] });

      const [applyResp, applyError] = await startApplyingDataImport(importId, {
        reapplyAlreadyImportedRecords: false,
        ignoreInvalidRecords: true,
        ignoreUpdatesInRecordsValidation: false,
        sendEmailNotificationWhenFinished: options.sendEmailNotificationWhenFinished,
      });

      if (applyError) {
        onError(applyError);
        return;
      }

      if (isMounted()) {
        setPollingState({ dataImportId: importId, applicationId: applyResp.applicationId });
      }
    },
    [isMounted, onStatusUpdate, onError]
  );
};

interface RollbackDataImportOptions {
  onStatusUpdate: (operationStatus: OperationStatus) => void;
  onComplete: () => void;
  onError: (error: ApiError) => void;
}

interface RollbackPollingState {
  dataImportId: string;
  rollbackId: string;
}

export const useRollbackDataImport = ({ onStatusUpdate, onComplete, onError }: RollbackDataImportOptions) => {
  const isMounted = useIsMounted();
  const [pollingState, setPollingState] = useState<RollbackPollingState>();

  const poll = useCallback(async () => {
    if (!pollingState) {
      return;
    }

    const [statusResp, statusError] = await getDataImportRollbackStatus(
      pollingState.dataImportId,
      pollingState.rollbackId
    );

    if (statusError?.type === "NotFound") {
      return;
    }

    if (statusError) {
      setPollingState(undefined);
      onError(statusError);
      return;
    }

    onStatusUpdate({
      statusText: statusResp.status,
      error: statusResp.error,
      details: [
        ["Processed records", statusResp.processedRecords.toLocaleString()],
        ["Failed records", statusResp.failedRecords.toLocaleString()],
      ],
    });

    if (statusResp.isCompleted) {
      setPollingState(undefined);

      if (statusResp.error) {
        onError({ message: statusResp.error });
      } else {
        onComplete();
      }
    }
  }, [pollingState, onStatusUpdate, onComplete, onError]);

  useSetInterval(poll, delayMs, { disabled: !pollingState });

  return useCallback(
    async (importId: string) => {
      onStatusUpdate({
        statusText: "Rollback started",
        details: [],
      });

      const [rollbackResp, rollbackError] = await startDataImportRollback(importId);

      if (rollbackError) {
        onError(rollbackError);
        return;
      }

      if (isMounted()) {
        setPollingState({ dataImportId: importId, rollbackId: rollbackResp.rollbackId });
      }
    },
    [isMounted, onStatusUpdate, onError]
  );
};
